import { useContext, useEffect, useState, useCallback, useMemo } from "react";
import { Col, DatePicker, Row, Table, Card } from "antd";
import { Context } from "../../context";
import { Fetch, Post } from "../../fetch";

import moment from "moment";
import { handleExportClick } from "../../utils";
import ExportButton from "../ExportButton";

const { Column } = Table;

const dateFormat = "YYYY-MM-DD";

// Filter Components
const DateRangeFilter = ({ value, onChange }) => (
  <Col span={6}>
    <DatePicker.RangePicker
      style={{ width: "100%" }}
      value={value}
      onChange={onChange}
      format={dateFormat}
      allowClear={true}
      placeholder={['Start Date', 'End Date']}
    />
  </Col>
);

const ExpiredSubscriptionsMetrics = () => {
  const { apiUrl, token } = useContext(Context);
  const [data, setData] = useState([]);
  const [expiredCount, setExpiredCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    total_pages: 0
  });
  const [filters, setFilters] = useState({
    date: [moment().startOf("month"), moment().endOf("month")],
    page: 1,
    limit: 10,
  });

  // Memoized date range key for comparison
  const dateKey = useMemo(() => {
    if (!filters.date || !filters.date[0] || !filters.date[1]) return null;
    return `${moment(filters.date[0]).format(dateFormat)}_${moment(filters.date[1]).format(dateFormat)}`;
  }, [filters.date]);

  // Data fetching function with proper memoization
  const fetchExpiredSubscriptionsData = useCallback(async () => {
    if (!dateKey) return;

    const [startDate, endDate] = dateKey.split('_');
    const offset = (filters.page - 1) * filters.limit;

    setLoading(true);
    Fetch(
      `${apiUrl}/reports/expired_subscriptions_range?start_date=${startDate}&end_date=${endDate}&limit=${filters.limit}&offset=${offset}`,
      token,
      (result) => {
        const report = result.data || [];
        setData(report);
        setExpiredCount(result?.pagination?.total || 0);
        setPagination(result.pagination || {
          total: 0,
          page: 1,
          limit: 10,
          total_pages: 0
        });
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching expired subscriptions data:', error);
        setLoading(false);
      }
    );
  }, [apiUrl, token, dateKey, filters.page, filters.limit]);

  // Initialize data on component mount and when filters change
  useEffect(() => {
    fetchExpiredSubscriptionsData();
  }, [fetchExpiredSubscriptionsData]);

  // Handle date range changes
  const handleDateChange = (value, dateString) => {

    // Handle cancel/clear case - set to default date range
    if (!value || !dateString || dateString.length === 0) {
      setFilters({
        ...filters,
        date: [moment().startOf("month"), moment().endOf("month")],
        page: 1, // Reset to first page when date changes
      });
      return;
    }

    // Validate dates before setting
    const startDate = moment(dateString[0], dateFormat);
    const endDate = moment(dateString[1], dateFormat);

    if (startDate.isValid() && endDate.isValid()) {
      setFilters({
        ...filters,
        date: [startDate, endDate],
        page: 1, // Reset to first page when date changes
      });
    }
  };

  // Handle table pagination changes
  const handleTableChange = (paginationInfo) => {
    setFilters({
      ...filters,
      page: paginationInfo.current,
      limit: paginationInfo.pageSize,
    });
  };

  return (
    <div className="manage-orders-wrapper">
      <Card>
        <Row style={{ marginBottom: 20 }} align="middle">
          <Col span={24}>
            <h3 style={{ margin: 0, marginBottom: 16 }}>
              <span>Expired Subscriptions</span>
            </h3>
          </Col>
        </Row>

        <Row style={{ marginBottom: 20 }} align="middle">
          <DateRangeFilter
            value={[
              moment(filters.date[0], dateFormat),
              moment(filters.date[1], dateFormat),
            ]}
            onChange={handleDateChange}
          />
          <Col span={18} style={{ textAlign: "right" }}>
            <strong>Expired Subscriptions Count: {expiredCount}</strong>
          </Col>
        </Row>

        <Table
          dataSource={data}
          loading={loading}
          size="small"
          rowKey="user_id"
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          }}
          onChange={handleTableChange}
        >
          <Column
            title="Email"
            align="center"
            dataIndex={"email"}
            render={(_, record) => (record.email ? record.email : "-")}
          />
          <Column
            title="Mobile"
            align="center"
            dataIndex={"mobile"}
            render={(_, record) => (record.mobile ? record.mobile : "-")}
          />
          <Column title="Plan" align="center" dataIndex={"plan"} />
          <Column
            title="Expired On"
            align="center"
            dataIndex={"expired_on"}
            render={(_, record) =>
              record.expired_on
                ? moment(record.expired_on).format(dateFormat)
                : "-"
            }
          />
        </Table>

        <ExportButton
          className="export-btn-only"
          onExport={() => {
            const startDate = moment(filters.date[0]).format(dateFormat);
            const endDate = moment(filters.date[1]).format(dateFormat);
            return handleExportClick(
              apiUrl,
              token,
              `expired_subscriptions_range?start_date=${startDate}&end_date=${endDate}`,
              `expired_subscriptions_${startDate}_${endDate}.csv`
            );
          }}
        />
      </Card>
    </div>
  );
};

export default ExpiredSubscriptionsMetrics;
